import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EXTRA_HIGHLIGHT_PLAN_TYPE, PROFESSIONAL_PLAN_TYPE } from '../../constants/PLAN_TYPES';
import PLAN_DISCOUNT from '../../constants/PLAN_DISCOUNT';

interface GroupType {
  id: number;
  description: string;
}

export interface InstallmentsType {
  total: number;
  price: number;
}


interface PeriodicityType {
  months: number;
  description: string;
}

export interface PlanType {
  id?: number;
  group?: GroupType;
  description?: string;
  category?: string;
  planCandidate?: {
    id?: number;
  },
  price?: number;
  installments?: InstallmentsType;
  defaultPrice?: number;
  discountPercentual?: number;
  isSuggested?: boolean;
  productsPlus?: any[];
  periodicity?: PeriodicityType;
  renovation?: {
    price?: number;
    installments?: InstallmentsType;
    periodicity?: PeriodicityType;
  },
  renovationPlanId?: number;
  subscriptionType?: SubscriptionType;
  basePlanId?: number;
  paymentModeId?: number;
  periodicityMonths?: number;
  mode?: string;
  installmentsTotal?: number;
  groupId?: number;
  subscriptionTypeId?: number;
  active?: string;
  name?: string;
  totalPrice?: number;
  numOfInstallments?: number;
  installmentPrice?: number;
}

interface SubscriptionType {
  id: number;
  description: number;
}

export interface EnvioTurboPlanType {
  planId: string;
  transactionId: string;
  installments: number;
  quantity: number;
  price: number;
  installmentPrice: number;
}

export interface SelectedPlanToSendToSubscriptionsType extends PlanType {}

export interface VisibleUpgradePlansType {
  groupId: number;
  style: string;
  name: string;
  highlight: string;
  ribbon: boolean;
  description: string;
}

interface BulletsArgumentsType {
  message: string;
  isEnabled: boolean;
}

interface HeadersType {
  [header: string]: string;
}

export interface SelectedUpgradePlansType {
  [PlanGroupId: string]: PlanType;
}

export interface RecommendedPlan {
  name: string;
  groupId: number;
  type: typeof EXTRA_HIGHLIGHT_PLAN_TYPE | typeof PROFESSIONAL_PLAN_TYPE;
}

interface State {
  plans?: PlanType[];
  plansHeaders?: HeadersType;
  professionalPlans?: PlanType[];
  professionalPlansHeaders?: HeadersType;
  selectedPlanOnPlansComponent?: PlanType;
  selectedPlanToSendToSubscriptions?: SelectedPlanToSendToSubscriptionsType | EnvioTurboPlanType;
  selectedPlan?: SelectedPlanToSendToSubscriptionsType | EnvioTurboPlanType;
  selectedNormalPlan?: SelectedPlanToSendToSubscriptionsType | EnvioTurboPlanType;
  selectedPixPlan?: SelectedPlanToSendToSubscriptionsType | EnvioTurboPlanType;  
  planDiscount?: string;
  directSalePlan?: any;
  limitedApplyPlan?: PlanType;
  limitedApplyPlanId?: number;
  selectedUpgradePlans?: SelectedUpgradePlansType;
  selectedPixUpgradePlans?: SelectedUpgradePlansType;
  visibleUpgradePlans?: VisibleUpgradePlansType[];
  visibleSemiannualUpgradePlans?: VisibleUpgradePlansType[];
  subscriptionPlanName?: string;
  proBulletArguments?: BulletsArgumentsType[];
  basicBulletArguments?: BulletsArgumentsType[];
  extraHighlightBulletArguments?: BulletsArgumentsType[];
  initialPlanGroupId: number;
  superApplyPlan?: EnvioTurboPlanType;
  recommendedPlan?: RecommendedPlan;
}

const initialState: State =  {
  plans: null,
  plansHeaders: null,
  professionalPlans: null,
  professionalPlansHeaders: null,
  selectedPlanOnPlansComponent: null,
  selectedPlanToSendToSubscriptions: null,
  selectedNormalPlan: null,
  selectedPixPlan: null,
  planDiscount: PLAN_DISCOUNT.DAYS,
  directSalePlan: null,
  limitedApplyPlan: null,
  limitedApplyPlanId: null,
  selectedUpgradePlans: null,
  selectedPixUpgradePlans: null,
  visibleUpgradePlans: null,
  subscriptionPlanName: null,
  proBulletArguments: null,
  basicBulletArguments: null,
  extraHighlightBulletArguments: null,
  initialPlanGroupId: null,
  superApplyPlan: null,
  recommendedPlan: null,
}

const plans = createSlice({
  name: 'plans',

  initialState,

  reducers: {
    setPlans(state, action: PayloadAction<any>) {
      state.plans = action.payload.plans;
      state.plansHeaders = action.payload.plansHeaders;
      state.professionalPlans = action.payload.professionalPlans;
      state.professionalPlansHeaders = action.payload.professionalPlansHeaders;
      state.selectedPlanOnPlansComponent = action.payload.selectedPlan;
      state.selectedPlanToSendToSubscriptions = action.payload.selectedPlan;
      state.selectedNormalPlan = action.payload.selectedNormalPlan;
      state.selectedPixPlan = action.payload.selectedPixPlan;
      state.directSalePlan = action.payload.directSalePlan;
    },

    setUpgradePlans(state, action: PayloadAction<any>) {
      state.selectedUpgradePlans = action.payload.selectedUpgradePlans;
      state.selectedPixUpgradePlans = action.payload.selectedPixUpgradePlans;
      state.visibleUpgradePlans = action.payload.visibleUpgradePlans;
      state.proBulletArguments = action.payload.proBulletArguments;
      state.basicBulletArguments = action.payload.basicBulletArguments;
      state.extraHighlightBulletArguments = action.payload.extraHighlightBulletArguments;
      state.selectedPlanOnPlansComponent = {};
      state.selectedPlanToSendToSubscriptions = {};
      state.plans = [];
      state.initialPlanGroupId = action.payload.initialPlanGroupId || null;
      state.recommendedPlan = action.payload.recommendedPlan || null;
    },

    updatePlanToSubscribe(state, action: PayloadAction<any>) {
      state.selectedPlanToSendToSubscriptions = action.payload.plan;
    },

    setPlanDiscount(state, action: PayloadAction<any>) {
      state.planDiscount = action.payload.planDiscount;
    },

    setLimitedApplyPlan(state, action: PayloadAction<any>) {
      state.limitedApplyPlan = action.payload.limitedApplyPlan;
      state.limitedApplyPlanId = action.payload.limitedApplyPlan.id;
    },

    setSuperApplyPlan(state, action: PayloadAction<any>) {
      state.superApplyPlan = action.payload.superApplyPlan;
      state.selectedPlanToSendToSubscriptions = action.payload.superApplyPlan;
    },

    removeRecommendedPlan(state) {
      state.recommendedPlan = null;
    },

    setPlan(state, action: PayloadAction<any>) {
      state.selectedPlanToSendToSubscriptions = action.payload.plan;
      state.selectedNormalPlan = action.payload.selectedNormalPlan;
      state.selectedPixPlan = action.payload.selectedPixPlan;
      state.subscriptionPlanName = action.payload.planName;
    }
  },
});

export const {
  setPlans,
  setUpgradePlans,
  updatePlanToSubscribe,
  setPlanDiscount,
  setLimitedApplyPlan,
  setSuperApplyPlan,
  removeRecommendedPlan,
  setPlan
} = plans.actions;

export default plans.reducer;
